import { useQuery } from '@tanstack/react-query'
import { AlertCircle, CreditCard, FileText, TrendingUp, Users } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { Alert, AlertDescription, AlertTitle } from '@quebon/ui/components/alert'
import { Button } from '@quebon/ui/components/button'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@quebon/ui/components/card'
import { Skeleton } from '@quebon/ui/components/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@quebon/ui/components/tabs'

import { adminApi } from '@/api/admin'
import type { DashboardStats } from '@/api/admin'
import { LcCenterDashboard } from '@/pages/LcCenterDashboard'

interface StatCardProps {
  title: string
  value: number | string
  subtitle?: string
  icon: React.ElementType
}

function StatCard({ title, value, subtitle, icon: Icon }: StatCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{subtitle ?? '전체 시스템 기준'}</CardDescription>
        <CardAction className="rounded-lg bg-muted p-2 text-muted-foreground">
          <Icon />
        </CardAction>
      </CardHeader>
      <CardContent>
        <p className="text-3xl font-semibold tracking-tight tabular-nums">{value}</p>
      </CardContent>
    </Card>
  )
}

function OverviewLoading() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4" aria-label="대시보드 통계를 불러오는 중">
      {Array.from({ length: 4 }, (_, index) => <Skeleton key={index} className="h-36 w-full" />)}
    </div>
  )
}

interface OverviewContentProps {
  stats: DashboardStats
}

function OverviewContent({ stats }: OverviewContentProps) {
  const navigate = useNavigate()

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard title="전체 사용자" value={stats.totalUsers} subtitle={`활성 ${stats.activeUsers}명`} icon={Users} />
        <StatCard title="전체 구독" value={stats.totalSubscriptions} subtitle={`활성 ${stats.activeSubscriptions}건`} icon={CreditCard} />
        <StatCard title="전체 콘텐츠" value={stats.totalContents} subtitle={`발행됨 ${stats.publishedContents}개`} icon={FileText} />
        <StatCard
          title="활성화율"
          value={stats.totalUsers ? `${Math.round((stats.activeUsers / stats.totalUsers) * 100)}%` : '0%'}
          subtitle="사용자 기준"
          icon={TrendingUp}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>사용자 유형별 현황</CardTitle>
            <CardDescription>전체 계정을 사용자 유형별로 집계합니다.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {Object.entries(stats.usersByType).length === 0 ? (
              <p className="py-6 text-center text-muted-foreground">데이터가 없습니다.</p>
            ) : Object.entries(stats.usersByType).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between gap-4 border-b pb-3 last:border-b-0 last:pb-0">
                <span>{type}</span>
                <span className="font-medium tabular-nums">{count}명</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>구독 상태별 현황</CardTitle>
            <CardDescription>현재 저장된 모든 구독의 상태 분포입니다.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {Object.entries(stats.subscriptionsByStatus).length === 0 ? (
              <p className="py-6 text-center text-muted-foreground">데이터가 없습니다.</p>
            ) : Object.entries(stats.subscriptionsByStatus).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between gap-4 border-b pb-3 last:border-b-0 last:pb-0">
                <span>{status}</span>
                <span className="font-medium tabular-nums">{count}건</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>빠른 작업</CardTitle>
          <CardDescription>자주 사용하는 운영 화면으로 이동합니다.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button type="button" onClick={() => navigate('/users')}>사용자 관리</Button>
          <Button type="button" variant="outline" onClick={() => navigate('/subscriptions')}>구독 관리</Button>
          <Button type="button" variant="outline" onClick={() => navigate('/contents')}>콘텐츠 관리</Button>
        </CardContent>
      </Card>
    </div>
  )
}

function SystemOverview() {
  const query = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: adminApi.getDashboardStats,
    staleTime: 60 * 1000,
  })

  if (query.isLoading) return <OverviewLoading />

  if (query.isError || !query.data) {
    return (
      <Alert variant="destructive">
        <AlertCircle />
        <AlertTitle>대시보드 통계를 불러오지 못했습니다.</AlertTitle>
        <AlertDescription>관리자 권한을 확인하고 잠시 후 다시 시도해주세요.</AlertDescription>
      </Alert>
    )
  }

  return <OverviewContent stats={query.data} />
}

export function AdminDashboard() {
  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">관리자 대시보드</h1>
        <p className="mt-1 text-muted-foreground">시스템 전체 현황과 러닝센터 운영 지표를 확인하세요.</p>
      </div>

      <Tabs defaultValue="overview">
        <TabsList variant="line">
          <TabsTrigger value="overview">전체 현황</TabsTrigger>
          <TabsTrigger value="lc-centers">LC 현황</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="pt-4">
          <SystemOverview />
        </TabsContent>
        <TabsContent value="lc-centers" className="pt-4">
          <LcCenterDashboard />
        </TabsContent>
      </Tabs>
    </div>
  )
}
